<!--
  Offer composer — shared by "Potential clients" (stock page) and "Potential interest"
  (people list). Administrators only (the API refuses the rest).

  One e-mail per client, in the client's language, with the XYLO-PRICING catalogue price
  of the client's zone (incoterm). Every value the e-mail depends on is on screen and
  editable before anything leaves: language, zone, price (per zone or per client),
  addresses, and the template of each language. The preview is rendered by the same
  function as the e-mail that is sent (offerTemplates.renderOffer).

  Sending goes through /api/potential-interest/send in batches of 5: the server checks
  that each address belongs to the contact record, skips unsubscribed contacts, sends
  through the SMTP service (Mail History) and records the offer.
-->
<template>
  <vs-popup fullscreen :title="popupTitle" :active.sync="isActive" class="pi-offer-popup" :style="{ zIndex: 53010 }">
    <div class="pi-offer">
      <div v-if="loading" class="mb-4">Loading catalogue prices and contacts...</div>
      <vs-alert v-if="error" color="danger" active="true" class="mb-4">{{ error }}</vs-alert>

      <template v-if="ctx && !loading">
        <div class="flex flex-wrap items-end mb-4">
          <div class="flex-grow mr-4 mb-2">
            <h4 class="mb-1">{{ productLabel('en') }}</h4>
            <div class="text-sm opacity-75">
              Product #{{ idproduct }} · sold per {{ ctx.product.uom || 'unit' }} ·
              {{ recipients.length }} client(s) selected
            </div>
          </div>
          <vs-input label="Reply-To" v-model="replyTo" class="mr-4 mb-2" style="min-width: 280px" />
          <vs-checkbox v-model="bccSales" class="mb-3">BCC sales@acaciabois.com</vs-checkbox>
        </div>

        <!-- 1. Prices per zone -->
        <h5 class="mb-2">1. Catalogue price per zone <small class="opacity-75">(XYLO-PRICING, automatic finishes included)</small></h5>
        <div class="pi-scroll mb-6">
          <table class="pi-table w-full">
            <thead>
              <tr>
                <th>Zone</th>
                <th class="text-right">Clients</th>
                <th class="text-right">Catalogue price</th>
                <th>Offered price (override)</th>
                <th>Check</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="z in zonesSorted" :key="z.key" :class="{ 'opacity-50': !countByZone[z.key] }">
                <td class="font-semibold">{{ z.incoterm_label || z.label }}</td>
                <td class="text-right">{{ countByZone[z.key] || 0 }}</td>
                <td class="text-right">
                  <span v-if="priceOf(z.key).matched">
                    {{ money(priceOf(z.key).amount) }} / {{ unitLabel(priceOf(z.key)) }}
                  </span>
                  <span v-else class="text-danger">No catalogue price</span>
                </td>
                <td>
                  <div class="flex items-center">
                    <input class="pi-input" type="number" step="0.01" min="0" v-model="zoneOverride[z.key]"
                      :placeholder="priceOf(z.key).matched ? String(priceOf(z.key).amount) : 'price'" />
                    <span class="ml-2 text-sm">EUR / {{ unitLabel(priceOf(z.key)) }}</span>
                  </div>
                </td>
                <td class="text-sm pi-notes">
                  <vs-chip v-if="priceOf(z.key).matched && priceOf(z.key).delivered" color="warning">Full-truck price</vs-chip>
                  <vs-chip v-if="priceOf(z.key).applied && priceOf(z.key).applied.length" color="primary">
                    + {{ priceOf(z.key).applied.join(', ') }}
                  </vs-chip>
                  <span v-if="priceOf(z.key).axis_note" class="text-warning">{{ priceOf(z.key).axis_note }}</span>
                  <span v-if="priceOf(z.key).source_cell" class="opacity-75">cell {{ priceOf(z.key).source_cell }}</span>
                  <span v-if="priceOf(z.key).source === 'override'" class="opacity-75">· price override</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="unitMismatch" class="text-sm text-warning mt-2">
            The catalogue prices this product per {{ unitMismatch }}, the ERP sells it per {{ ctx.product.uom }}:
            the e-mail states the catalogue unit, never a converted price.
          </div>
        </div>

        <!-- 2. Recipients -->
        <div class="flex flex-wrap items-center mb-2">
          <h5 class="flex-grow mb-0">2. Recipients <small class="opacity-75">({{ sendable.length }} ready to send)</small></h5>
          <vs-button size="small" type="border" class="mr-2" @click="includeAll(true)">Select all sendable</vs-button>
          <vs-button size="small" type="border" class="mr-4" @click="includeAll(false)">Unselect all</vs-button>
          <span class="text-sm mr-2">Zone for clients without one:</span>
          <select class="pi-select" v-model="bulkZone" @change="applyBulkZone">
            <option value="">—</option>
            <option v-for="z in zonesSorted" :key="z.key" :value="z.key">{{ z.incoterm_label || z.label }}</option>
          </select>
        </div>
        <div class="pi-scroll mb-6">
          <table class="pi-table w-full">
            <thead>
              <tr>
                <th style="width: 40px">Send</th>
                <th>Client</th>
                <th>Why</th>
                <th>Country</th>
                <th>Language</th>
                <th>Zone</th>
                <th>Price (EUR)</th>
                <th class="text-right">Last paid</th>
                <th>E-mail to</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in recipients" :key="r.idperson" :class="{ 'pi-row-off': !r.include }">
                <td><vs-checkbox v-model="r.include" :disabled="hardBlocked(r)" /></td>
                <td class="pi-name">
                  <a :href="'/web/person/' + r.idperson" target="_blank" rel="noopener">{{ r.name }}</a>
                  <div class="text-xs opacity-75" v-if="r.broker_name">Broker: {{ r.broker_name }}</div>
                </td>
                <td><vs-chip :color="reasonColor(r.reason)">{{ reasonLabel(r.reason) }}</vs-chip></td>
                <td>{{ r.country_code || '—' }}</td>
                <td>
                  <select class="pi-select" v-model="r.lang" @change="ensureSignature(r.lang)">
                    <option v-for="l in LANGS" :key="l" :value="l">{{ l.toUpperCase() }}</option>
                  </select>
                  <span v-if="r.lang_assumed" class="text-xs text-warning" title="No language on the contact record: deduced from the country">?</span>
                </td>
                <td>
                  <select class="pi-select" v-model="r.zone">
                    <option :value="null">— choose —</option>
                    <option v-for="z in zonesSorted" :key="z.key" :value="z.key">{{ z.incoterm_label || z.label }}</option>
                  </select>
                  <div v-if="r.zone_note" class="text-xs" :class="r.zone_assumed ? 'text-warning' : 'opacity-75'"
                    :title="r.zone_note">{{ r.zone_assumed ? 'deduced: ' : '' }}{{ r.zone_note }}</div>
                </td>
                <td>
                  <input class="pi-input" type="number" step="0.01" min="0" v-model="r.priceOverride"
                    :placeholder="zonePriceAmount(r.zone) != null ? String(zonePriceAmount(r.zone)) : 'price'" />
                </td>
                <td class="text-right">
                  <span v-if="r.last_paid_price != null" :title="'Last invoice ' + r.last_paid_date">
                    {{ money(r.last_paid_price) }} / {{ ctx.product.uom }}
                    <a href="#" class="text-xs block" @click.prevent="r.priceOverride = String(r.last_paid_price)">use</a>
                  </span>
                  <span v-else class="opacity-50">—</span>
                </td>
                <td class="pi-emails">
                  <div v-if="!r.emails.length" class="text-danger text-sm">No e-mail on the record</div>
                  <label v-for="e in r.emails" :key="e.email" class="block text-sm">
                    <input type="checkbox" :value="e.email" v-model="r.to" />
                    {{ e.email }} <span class="opacity-50">({{ e.source }})</span>
                  </label>
                </td>
                <td class="text-sm">
                  <div v-for="p in problems(r)" :key="p" class="text-danger">{{ p }}</div>
                  <div v-for="w in r.warnings" :key="w" class="text-warning">{{ w }}</div>
                  <div v-if="r.result" :class="resultClass(r.result.status)">
                    {{ r.result.status }}<span v-if="r.result.error"> — {{ r.result.error }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 3. Templates -->
        <h5 class="mb-2">3. Message <small class="opacity-75">(one template per language — plain text)</small></h5>
        <div class="flex flex-wrap items-center mb-2">
          <vs-button v-for="l in langsInUse" :key="l" size="small" class="mr-2 mb-2"
            :type="l === langTab ? 'filled' : 'border'" @click="langTab = l">
            {{ LANG_LABELS[l] }} ({{ countByLang[l] || 0 }})
          </vs-button>
          <vs-button size="small" color="warning" type="flat" class="mb-2" @click="resetTemplate(langTab)">
            Reset {{ langTab.toUpperCase() }} to default
          </vs-button>
        </div>
        <div v-if="templates[langTab]" class="vx-row mb-6">
          <div class="vx-col w-full lg:w-1/2">
            <label class="text-sm">Subject</label>
            <input class="pi-input w-full mb-3" v-model="templates[langTab].subject" />
            <label class="text-sm">Opening — clients who bought or asked for a quote</label>
            <input class="pi-input w-full mb-3" v-model="templates[langTab].intro_interest" />
            <label class="text-sm">Opening — suggested clients (bought together, similar product, same area)</label>
            <input class="pi-input w-full mb-3" v-model="templates[langTab].intro_suggestion" />
            <label class="text-sm">Opt-out line (small print, leave empty to remove)</label>
            <input class="pi-input w-full mb-3" v-model="templates[langTab].optout" />
          </div>
          <div class="vx-col w-full lg:w-1/2">
            <label class="text-sm">Body</label>
            <textarea class="pi-textarea w-full" rows="9" v-model="templates[langTab].body"></textarea>
            <div class="text-xs opacity-75 mt-1">
              Variables: <code v-pre>{{intro}}</code> <code v-pre>{{product}}</code> <code v-pre>{{price}}</code>
              <code v-pre>{{incoterm}}</code> <code v-pre>{{packaging}}</code> — a line whose variables are all empty
              is removed. The greeting ("{{ helloExample }}"), the closing and the signature are added automatically.
            </div>
          </div>
        </div>

        <!-- 4. Preview -->
        <h5 class="mb-2">4. Preview</h5>
        <div class="flex items-center mb-2">
          <select class="pi-select mr-2" v-model="previewId" style="min-width: 320px">
            <option v-for="r in recipients" :key="r.idperson" :value="r.idperson">
              {{ r.name }} ({{ r.lang.toUpperCase() }}{{ r.zone ? ' · ' + zoneLabel(r.zone) : '' }})
            </option>
          </select>
        </div>
        <div v-if="previewRecipient" class="pi-preview mb-6">
          <div class="mb-1"><b>To:</b> {{ previewRecipient.to.join('; ') || '—' }}</div>
          <div class="mb-1"><b>Reply-To:</b> {{ replyTo }}</div>
          <div class="mb-3"><b>Subject:</b> {{ preview.subject }}</div>
          <div v-if="!effectivePrice(previewRecipient)" class="text-danger mb-2">No price for this client: the e-mail cannot be sent.</div>
          <div class="pi-preview-body">
            <div>{{ preview.hello }}</div>
            <div class="mt-3" v-html="preview.message_html"></div>
            <div class="mt-3" v-html="preview.goodbye_html"></div>
            <div class="mt-3" v-html="signatures[previewRecipient.lang] || ''"></div>
          </div>
        </div>

        <!-- 5. Send -->
        <div v-if="sending || report" class="mb-3">
          <vs-progress :percent="progress" color="primary"></vs-progress>
          <div class="text-sm mt-1">
            {{ sentCount }} sent · {{ skippedCount }} skipped · {{ failedCount }} failed
            <span v-if="sending"> — sending {{ doneCount }}/{{ totalToSend }}...</span>
          </div>
        </div>
        <vs-alert v-if="sendError" color="danger" active="true" class="mb-3">{{ sendError }}</vs-alert>
        <div v-if="sendable.length > 50 && !sending" class="text-sm text-warning mb-2">
          {{ sendable.length }} e-mails in one go: the OVH mailbox may throttle large batches (failures are listed per
          client and can be re-sent). Several smaller rounds are safer.
        </div>
        <vs-button class="w-full" icon-pack="feather" icon="icon-send" :disabled="!sendable.length || sending"
          @click="confirmSend">
          {{ sending ? 'Sending...' : `Send ${sendable.length} offer${sendable.length > 1 ? 's' : ''}` }}
        </vs-button>
      </template>
    </div>
  </vs-popup>
</template>

<script>
import axios from '@/axios.js'
import { getSignatureQuote } from '@/helpers/mail-signature.js'
import {
  LANGS, LANG_LABELS, cloneDefaults, normLang, renderOffer, productText, defaultRecipients,
  helloText, REASON_LABELS
} from './offerTemplates'

const BATCH = 5
const RECENT_OFFER_DAYS = 30
const UNIT_NAMES = { piece: 'piece', roll: 'roll', linear_m: 'linear m', m2: 'm²', m3: 'm³' }

export default {
  name: 'OfferMailComposer',
  props: {
    active: { type: Boolean, default: false },
    idproduct: { type: [Number, String], default: null },
    // [{ idperson, reason: 'bought'|'quoted'|'related'|'similar'|'area', open_order? }]
    selection: { type: Array, default: () => [] }
  },
  data () {
    return {
      LANGS,
      LANG_LABELS,
      loading: false,
      error: null,
      ctx: null,
      recipients: [],
      zoneOverride: {},
      templates: cloneDefaults(),
      signatures: {},
      langTab: 'fr',
      previewId: null,
      replyTo: 'sales@acaciabois.com',
      bccSales: false,
      bulkZone: '',
      sending: false,
      sendError: null,
      report: null,
      totalToSend: 0
    }
  },
  computed: {
    isActive: {
      get () { return this.active },
      set (val) { this.$emit('update:active', val) }
    },
    popupTitle () {
      return this.ctx ? `Send an offer — ${this.productLabel('en')}` : 'Send an offer'
    },
    zonesSorted () {
      const zones = (this.ctx && this.ctx.zones) || []
      return zones.slice().sort((a, b) => (a.sort || 0) - (b.sort || 0))
    },
    countByZone () {
      const out = {}
      this.recipients.filter(r => r.include).forEach(r => { if (r.zone) out[r.zone] = (out[r.zone] || 0) + 1 })
      return out
    },
    countByLang () {
      const out = {}
      this.recipients.filter(r => r.include).forEach(r => { out[r.lang] = (out[r.lang] || 0) + 1 })
      return out
    },
    langsInUse () {
      const set = new Set(this.recipients.filter(r => r.include).map(r => r.lang))
      if (!set.size) this.recipients.forEach(r => set.add(r.lang))
      const list = LANGS.filter(l => set.has(l))
      return list.length ? list : ['fr']
    },
    unitMismatch () {
      const units = new Set()
      Object.values((this.ctx && this.ctx.prices) || {}).forEach(p => { if (p && p.matched) units.add(p.unit) })
      const other = Array.from(units).filter(u => u !== 'piece' && u !== 'roll' && !(u === 'm3' && /m3/i.test(this.ctx.product.uom || '')))
      return other.length ? other.map(u => UNIT_NAMES[u] || u).join(', ') : null
    },
    sendable () {
      return this.recipients.filter(r => r.include && !this.problems(r).length)
    },
    previewRecipient () {
      return this.recipients.find(r => r.idperson === this.previewId) || null
    },
    preview () {
      const r = this.previewRecipient
      if (!r) return { subject: '', hello: '', message_html: '', goodbye_html: '' }
      return this.renderFor(r)
    },
    helloExample () {
      return helloText({ iscompany: true }, this.langTab)
    },
    sentCount () { return this.report ? this.report.filter(x => x.status === 'sent').length : 0 },
    skippedCount () { return this.report ? this.report.filter(x => x.status === 'skipped').length : 0 },
    failedCount () { return this.report ? this.report.filter(x => x.status === 'failed').length : 0 },
    doneCount () { return this.report ? this.report.length : 0 },
    progress () { return this.totalToSend ? Math.round(100 * this.doneCount / this.totalToSend) : 0 }
  },
  watch: {
    active: {
      immediate: true,
      handler (val) { if (val) this.load() }
    },
    langsInUse (list) {
      if (list.indexOf(this.langTab) === -1) this.langTab = list[0]
      list.forEach(l => this.ensureSignature(l))
    }
  },
  methods: {
    money (value) {
      return (Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    productLabel (lang) {
      return this.ctx ? productText(this.ctx.product.labels, lang) : ''
    },
    priceOf (zone) {
      return (this.ctx && this.ctx.prices && this.ctx.prices[zone]) || { matched: false }
    },
    unitLabel (price) {
      if (!price || !price.matched) return (this.ctx && this.ctx.product.uom) || 'unit'
      if (price.unit === 'roll' && price.roll_length_m) return `roll (${price.roll_length_m} m)`
      return UNIT_NAMES[price.unit] || price.unit
    },
    zoneLabel (key) {
      const z = this.zonesSorted.find(x => x.key === key)
      return z ? (z.incoterm_label || z.label) : key
    },
    zonePriceAmount (zone) {
      if (!zone) return null
      const o = this.zoneOverride[zone]
      if (o !== undefined && o !== null && String(o).trim() !== '' && Number(o) > 0) return Number(o)
      const p = this.priceOf(zone)
      return p.matched ? p.amount : null
    },
    // Prix réellement écrit dans l'offre : saisie client > saisie zone > catalogue.
    effectivePrice (r) {
      if (!r.zone) return null
      const cat = this.priceOf(r.zone)
      const zone = this.zonesSorted.find(z => z.key === r.zone)
      const own = r.priceOverride !== undefined && r.priceOverride !== null && String(r.priceOverride).trim() !== ''
        ? Number(r.priceOverride) : null
      const amount = own > 0 ? own : this.zonePriceAmount(r.zone)
      if (!(amount > 0)) return null
      return {
        amount,
        unit: cat.matched ? cat.unit : 'piece',
        roll_length_m: cat.matched ? cat.roll_length_m : null,
        pieces_per_pallet: cat.matched ? cat.pieces_per_pallet : null,
        delivered: zone ? zone.delivered !== false : !!cat.delivered
      }
    },
    hardBlocked (r) {
      return r.unsubscribed || !r.emails.length
    },
    problems (r) {
      const out = []
      if (r.unsubscribed) out.push('Unsubscribed')
      if (!r.emails.length) out.push('No e-mail')
      else if (!r.to.length) out.push('No address ticked')
      if (!r.zone) out.push('Choose a zone')
      else if (!this.effectivePrice(r)) out.push('No price')
      return out
    },
    reasonLabel (reason) { return REASON_LABELS[reason] || reason || '—' },
    reasonColor (reason) {
      return { bought: 'success', quoted: 'warning', related: 'primary', similar: 'primary', area: 'dark' }[reason] || 'dark'
    },
    resultClass (status) {
      return status === 'sent' ? 'text-success' : status === 'skipped' ? 'text-warning' : 'text-danger'
    },
    renderFor (r) {
      return renderOffer(this.templates[r.lang], {
        lang: r.lang,
        person: r,
        reason: r.reason,
        labels: this.ctx.product.labels,
        price: this.effectivePrice(r) || {}
      })
    },
    async ensureSignature (lang) {
      const l = normLang(lang)
      if (this.signatures[l] !== undefined) return
      this.$set(this.signatures, l, '')
      try {
        this.$set(this.signatures, l, await getSignatureQuote(l))
      } catch (e) {
        this.$set(this.signatures, l, '')
      }
    },
    resetTemplate (lang) {
      const fresh = cloneDefaults()
      this.$set(this.templates, lang, fresh[lang])
    },
    includeAll (on) {
      this.recipients.forEach(r => { r.include = on && !this.hardBlocked(r) })
    },
    applyBulkZone () {
      if (!this.bulkZone) return
      this.recipients.forEach(r => { if (!r.zone) r.zone = this.bulkZone })
      this.bulkZone = ''
    },
    async load () {
      this.error = null
      this.report = null
      this.sendError = null
      this.ctx = null
      this.recipients = []
      this.zoneOverride = {}
      this.templates = cloneDefaults()
      const byId = new Map()
      ;(this.selection || []).forEach(s => { if (s && s.idperson && !byId.has(s.idperson)) byId.set(s.idperson, s) })
      if (!this.idproduct || !byId.size) {
        this.error = 'No client selected.'
        return
      }
      this.loading = true
      try {
        const { data } = await axios.post('/potential-interest/offer-context', {
          idproduct: Number(this.idproduct),
          idpersons: Array.from(byId.keys())
        })
        this.ctx = data
        const zoneKeys = new Set((data.zones || []).map(z => z.key))
        this.recipients = (data.persons || []).map(p => {
          const sel = byId.get(p.idperson) || {}
          const warnings = []
          if (p.last_offer_at) {
            const days = Math.floor((Date.now() - Date.parse(`${p.last_offer_at}T12:00:00Z`)) / 86400000)
            warnings.push(`Offer already sent on ${p.last_offer_at}`)
            if (days < RECENT_OFFER_DAYS) warnings.push(`(less than ${RECENT_OFFER_DAYS} days ago: unticked)`)
          }
          if (sel.open_order) warnings.push('Order in progress for this product')
          if (p.isbroker) warnings.push('Broker record')
          const recentOffer = p.last_offer_at &&
            (Date.now() - Date.parse(`${p.last_offer_at}T12:00:00Z`)) / 86400000 < RECENT_OFFER_DAYS
          const to = defaultRecipients(p.emails)
          const zone = p.zone && zoneKeys.has(p.zone) ? p.zone : null
          return Object.assign({}, p, {
            lang: normLang(p.lang),
            zone,
            reason: sel.reason || 'related',
            to,
            priceOverride: '',
            warnings,
            result: null,
            include: !p.unsubscribed && p.emails.length > 0 && !recentOffer && !sel.open_order
          })
        })
        this.previewId = (this.recipients.find(r => r.include) || this.recipients[0] || {}).idperson || null
        this.langsInUse.forEach(l => this.ensureSignature(l))
        this.langTab = this.langsInUse[0]
      } catch (e) {
        const d = e.response && e.response.data
        this.error = (d && (d.message || d.detail)) || e.message || 'Could not load prices and contacts.'
      } finally {
        this.loading = false
      }
    },
    confirmSend () {
      const n = this.sendable.length
      if (!n) return
      // window.confirm : toujours au-dessus des popups Vuesax (z-index), contrairement à $vs.dialog.
      const langs = Array.from(new Set(this.sendable.map(r => r.lang.toUpperCase()))).join(', ')
      if (!window.confirm(`Send ${n} offer e-mail(s) (${langs}) for "${this.productLabel('en')}"?`)) return
      this.sendAll()
    },
    async sendAll () {
      const items = this.sendable.map(r => {
        const m = this.renderFor(r)
        const price = this.effectivePrice(r)
        return {
          idperson: r.idperson,
          to: r.to.slice(),
          subject: m.subject,
          hello: m.hello,
          message_html: m.message_html,
          goodbye_html: m.goodbye_html,
          signature_html: this.signatures[r.lang] || '',
          lang: r.lang,
          zone: r.zone,
          unit_price: price ? price.amount : null,
          price_unit: price ? price.unit : null,
          reason: r.reason
        }
      })
      this.sending = true
      this.sendError = null
      this.report = []
      this.totalToSend = items.length
      this.recipients.forEach(r => { r.result = null })
      const byId = new Map(this.recipients.map(r => [r.idperson, r]))
      try {
        for (let i = 0; i < items.length; i += BATCH) {
          const chunk = items.slice(i, i + BATCH)
          const { data } = await axios.post('/potential-interest/send', {
            idproduct: Number(this.idproduct),
            reply_to: this.replyTo,
            bcc: this.bccSales ? 'sales@acaciabois.com' : '',
            items: chunk
          })
          ;(data.results || []).forEach(res => {
            this.report.push(res)
            const r = byId.get(res.idperson)
            if (r) {
              r.result = res
              if (res.status === 'sent') r.include = false
            }
          })
        }
      } catch (e) {
        const d = e.response && e.response.data
        this.sendError = `Sending stopped: ${(d && (d.message || d.detail)) || e.message}. ` +
          'The offers listed as sent did leave; the others were not sent.'
      } finally {
        this.sending = false
      }
      const sent = this.sentCount
      this.$vs.notify({
        color: this.failedCount || this.sendError ? 'warning' : 'success',
        title: 'Offers',
        text: `${sent} sent, ${this.skippedCount} skipped, ${this.failedCount} failed.`,
        position: 'top-right'
      })
      this.$emit('sent', { idproduct: Number(this.idproduct), results: this.report.slice() })
    }
  }
}
</script>

<style lang="scss">
.pi-offer {
  .pi-scroll {
    overflow-x: auto;
  }

  .pi-table {
    border-collapse: collapse;
    font-size: 0.88rem;

    th,
    td {
      padding: 0.4rem 0.5rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      vertical-align: top;
    }

    th {
      text-align: left;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.7rem;
      opacity: 0.7;
      white-space: nowrap;
    }
  }

  .pi-row-off {
    opacity: 0.55;
  }

  .pi-name {
    min-width: 180px;
  }

  .pi-emails {
    min-width: 220px;
    max-width: 320px;
    word-break: break-all;
  }

  .pi-notes {
    max-width: 380px;

    .con-vs-chip {
      display: inline-flex;
      margin: 0 4px 4px 0;
    }
  }

  .pi-input,
  .pi-select,
  .pi-textarea {
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    padding: 0.35rem 0.5rem;
    font-size: 0.88rem;
    background: transparent;
    color: inherit;
    font-family: inherit;
  }

  .pi-input[type='number'] {
    width: 110px;
  }

  .pi-textarea {
    resize: vertical;
    line-height: 1.45;
  }

  .pi-preview {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    padding: 1rem;
  }

  .pi-preview-body {
    background: #fff;
    color: #222;
    border-radius: 6px;
    padding: 1rem;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    max-width: 720px;
  }
}
</style>
